import React, { useState } from 'react';
import { Container, Text, Button } from 'components';
import { FiCopy, FiExternalLink, FiRefreshCw, FiEye } from 'react-icons/fi';
import { InviteData } from './types';

interface InviteListProps {
  invites: InviteData[];
  getStatusIcon: (status: InviteData['status']) => React.ReactNode;
  getStatusColor: (status: InviteData['status']) => string;
  getStatusText: (status: InviteData['status']) => string;
  invitationTracking?: Array<{
    createdByUserGuid: string;
    createdByUserEmail: string;
    targetEmailAddress: string;
    pistilRetailerId: string;
    storeName: string;
    trackingStatus: string;
    createdAt: string;
  }>;
}

const InviteList: React.FC<InviteListProps> = ({
  invites,
  getStatusIcon,
  getStatusColor,
  getStatusText,
  invitationTracking = []
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const mappedInvites: InviteData[] = invitationTracking.map((tracking, index) => ({
    id: tracking.pistilRetailerId || `tracking-${index}`,
    retailerName: tracking.storeName,
    retailerEmail: tracking.targetEmailAddress,
    invitedBy: tracking.createdByUserEmail,
    brandName: '', 
    invitedAt: new Date(tracking.createdAt),
    status: tracking.trackingStatus as InviteData['status'],
    inviteLink: '' 
  }));

  // Ordenar por fecha de envío en orden decreciente (más recientes primero)
  const displayInvites = mappedInvites.sort((a, b) => b.invitedAt.getTime() - a.invitedAt.getTime());

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getDaysAgo = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1d ago';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}m ago`;
  };

  const handleResendInvite = (invite: InviteData) => {
    console.log('Reenviando invitación:', invite);
  };

  const handleOpenLink = (link: string) => {
    window.open(link, '_blank');
  };

  if (displayInvites.length === 0) {
    return (
      <div className="empty-state">
        <FiRefreshCw size={48} />
        <Text textStyle="h3" color="text">
          No invitations yet
        </Text>
        <Text textStyle="s1" color="text">
          When you send your first invitation, it will appear here for tracking.
        </Text>
      </div>
    );
  }

  return (
    <div className="pos-invite-list">
          <div className="unified-header">
            <div className="header-left">
              <div className="header-title">
                <Text textStyle="h3" color="text">
                  Invitation Tracking
                </Text>
              </div>
              <div className="header-subtitle">
                <Text textStyle="s1" color="text">
                  {`${displayInvites.length} total invitation${displayInvites.length !== 1 ? 's' : ''}`}
                </Text>
              </div>
            </div>
            
            <div className="header-stats">
              <div className="stat-item">
                <div className="stat-number">
                  <Text textStyle="h3" color="brand">
                    {`${displayInvites.filter(i => i.status?.toLowerCase() === 'sent').length}`}
                  </Text>
                </div>
                <div className="stat-label">
                  <Text textStyle="support" color="text">
                    Sent
                  </Text>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  <Text textStyle="h3" color="brand">
                    {`${displayInvites.filter(i => i.status?.toLowerCase() === 'opened').length}`}
                  </Text>
                </div>
                <div className="stat-label">
                  <Text textStyle="support" color="text">
                    Opened
                  </Text>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  <Text textStyle="h3" color="brand">
                    {`${displayInvites.filter(i => i.status?.toLowerCase() === 'completed').length}`}
                  </Text>
                </div>
                <div className="stat-label">
                  <Text textStyle="support" color="text">
                    Completed
                  </Text>
                </div>
              </div>
            </div>
          </div>
          <div className="invite-table">
            <div className="table-header">
              <div className="table-cell header-cell">Retailer</div>
              <div className="table-cell header-cell">Email</div>
              <div className="table-cell header-cell">Status</div>
              <div className="table-cell header-cell">Sent</div>
            </div>
            
            <div className="table-body">
              {displayInvites.map((invite) => (
                <div key={invite.id} className={`table-row status-${invite.status?.toLowerCase()}`}>
                  <div className="table-cell">
                    <div className="retailer-info">
                      <Text textStyle="s1" color="text">
                        {invite.retailerName}
                      </Text>
                      {invite.brandName && (
                        <Text textStyle="support" color="text">
                          {invite.brandName}
                        </Text>
                      )}
                    </div>
                  </div>
                  
                  <div className="table-cell">
                    <Text textStyle="s1" color="text">
                      {invite.retailerEmail}
                    </Text>
                  </div>
                  
                  <div className="table-cell">
                    <div className={`status-badge compact status-${invite.status}`}>
                      {getStatusIcon(invite.status)}
                      <Text textStyle="support">
                        {getStatusText(invite.status)}
                      </Text>
                    </div>
                  </div>
                  
                  <div className="table-cell">
                    <div className="date-info">
                      <Text textStyle="support" color="text">
                        {formatDate(invite.invitedAt)}
                      </Text>
                      <Text textStyle="support" color="text">
                        {getDaysAgo(invite.invitedAt)}
                      </Text>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
    </div>
  );
};

export default InviteList;
